import React, { useState, useEffect, useCallback } from 'react';
import { useLanguage } from '../context/LanguageContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Switch } from '../components/ui/switch';
import { toast } from 'sonner';
import { Lock, Loader2, Save, Trash2, Plus, ShoppingBag, Settings } from 'lucide-react';
import axios from 'axios';
import Dashboard from './AdminDashboard';
import NotificationBell from '../components/NotificationBell';
import { useConfirm } from '../components/ConfirmProvider';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AdminPage = () => {
  const { t } = useLanguage();
  const confirm = useConfirm();
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [credentials, setCredentials] = useState({ username: '', password: '' });
  const [authHeader, setAuthHeader] = useState('');
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('dashboard');

  // Menu state
  const [menuItems, setMenuItems] = useState([]);
  const [editingItem, setEditingItem] = useState(null);
  const [showAddForm, setShowAddForm] = useState(false);

  // Shabbat state
  const [shabbatData, setShabbatData] = useState({});
  const [savingShabbat, setSavingShabbat] = useState(false);

  // Orders state
  const [orders, setOrders] = useState([]);
  const [substitutionOrder, setSubstitutionOrder] = useState(null);
  const [subItemIndex, setSubItemIndex] = useState(0);
  const [subNote, setSubNote] = useState('');

  // Hotels state
  const [hotels, setHotels] = useState([]);
  const [editingHotel, setEditingHotel] = useState(null);
  const [showAddHotel, setShowAddHotel] = useState(false);
  const [hotelForm, setHotelForm] = useState({ name: '', url: '', image_url: '', order: 0 });

  // Shabbat guests state
  const [shabbatGuests, setShabbatGuests] = useState([]);
  const [shabbatGuestsSummary, setShabbatGuestsSummary] = useState(null);
  const [approvingGuest, setApprovingGuest] = useState(null);

  // Settings state
  const [settings, setSettings] = useState({
    pickup_address: '',
    paypal_client_id: '',
    paypal_secret: '',
    smtp_email: '',
    whatsapp_orders: '',
    whatsapp_contact: '',
    delivery_zones: {}
  });
  const [savingSettings, setSavingSettings] = useState(false);
  const [syncingShabbat, setSyncingShabbat] = useState(false);

  const getAuth = useCallback(() => {
    return { headers: { Authorization: `Bearer ${authHeader}` } };
  }, [authHeader]);

  const handleSyncShabbatTimes = async () => {
    setSyncingShabbat(true);
    try {
      const res = await axios.post(`${API}/admin/shabbat/auto-fetch`, null, getAuth());
      toast.success(`Sincronizado: velas ${res.data.candle_lighting} · havdala ${res.data.havdalah}`);
      await fetchData();
    } catch (e) {
      toast.error('No se pudo sincronizar. Reintenta en unos segundos.');
    } finally {
      setSyncingShabbat(false);
    }
  };

  const handleLogin = async () => {
    try {
      const response = await axios.post(`${API}/admin/login`, {
        username: (credentials.username || '').trim(),
        password: (credentials.password || '').trim()
      });
      setAuthHeader(response.data.token);
      setIsAuthenticated(true);
      toast.success('Acceso concedido');
    } catch {
      toast.error('Credenciales incorrectas');
    }
  };

  const fetchData = useCallback(async (showLoading = false) => {
    if (!authHeader) return;
    if (showLoading) setLoading(true);
    try {
      const [menuRes, shabbatRes, ordersRes, settingsRes, hotelsRes, guestsRes, summaryRes] = await Promise.all([
        axios.get(`${API}/menu/all`, getAuth()),
        axios.get(`${API}/shabbat/schedule`),
        axios.get(`${API}/admin/orders?include_unpaid=true`, getAuth()),
        axios.get(`${API}/admin/settings`, getAuth()),
        axios.get(`${API}/hotels`),
        axios.get(`${API}/admin/shabbat-guests`, getAuth()),
        axios.get(`${API}/admin/shabbat-guests/summary`, getAuth())
      ]);
      setMenuItems(menuRes.data);
      setShabbatData(shabbatRes.data);
      setOrders(ordersRes.data);
      setSettings(settingsRes.data);
      setHotels(hotelsRes.data);
      setShabbatGuests(guestsRes.data);
      setShabbatGuestsSummary(summaryRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  }, [authHeader, getAuth]);

  useEffect(() => {
    if (isAuthenticated) fetchData(true);
  }, [isAuthenticated, fetchData]);

  const handleDeleteItem = async (itemId) => {
    const ok = await confirm({ title: 'Eliminar platillo', description: '¿Estás seguro?', confirmText: 'Eliminar' });
    if (!ok) return;
    try {
      await axios.delete(`${API}/admin/menu/${itemId}`, getAuth());
      toast.success('Platillo eliminado');
      fetchData();
    } catch {
      toast.error('Error al eliminar');
    }
  };

  const handleSaveHotel = async () => {
    try {
      if (editingHotel) {
        await axios.put(`${API}/admin/hotels/${editingHotel.id}`, hotelForm, getAuth());
        toast.success('Hotel actualizado');
      } else {
        await axios.post(`${API}/admin/hotels`, hotelForm, getAuth());
        toast.success('Hotel agregado');
      }
      setEditingHotel(null);
      setShowAddHotel(false);
      setHotelForm({ name: '', url: '', image_url: '', order: 0 });
      fetchData();
    } catch { toast.error('Error'); }
  };

  const handleDeleteHotel = async (id) => {
    const ok = await confirm({ title: 'Eliminar hotel', description: '¿Estás seguro?', confirmText: 'Eliminar' });
    if (!ok) return;
    try {
      await axios.delete(`${API}/admin/hotels/${id}`, getAuth());
      toast.success('Hotel eliminado');
      fetchData();
    } catch { toast.error('Error'); }
  };

  const handleApproveGuest = async (guestId, status) => {
    try {
      await axios.put(`${API}/admin/shabbat-guests/${guestId}/approve`, { status }, getAuth());
      toast.success(status === 'approved' ? 'Aprobado - email enviado' : 'Rechazado - email enviado');
      fetchData();
    } catch { toast.error('Error'); }
  };

  const handleDeleteGuest = async (guestId) => {
    const ok = await confirm({
      title: 'Eliminar registro',
      description: '¿Eliminar este registro de invitado de forma permanente?',
      confirmText: 'Eliminar',
    });
    if (!ok) return;
    try {
      await axios.delete(`${API}/admin/shabbat-guests/${guestId}`, getAuth());
      toast.success('Registro eliminado');
      fetchData();
    } catch { toast.error('Error al eliminar'); }
  };

  const handleBulkDeleteGuests = async (status) => {
    const label = status === 'approved' ? 'aprobados' : status === 'rejected' ? 'rechazados' : status;
    const ok = await confirm({
      title: `Eliminar todos ${label}`,
      description: 'Esta acción no se puede deshacer.',
      confirmText: 'Borrar todos',
    });
    if (!ok) return;
    try {
      const res = await axios.delete(`${API}/admin/shabbat-guests?status=${status}`, getAuth());
      toast.success(`${res.data.count} eliminados`);
      fetchData();
    } catch { toast.error('Error en eliminación masiva'); }
  };

  const handleDeleteOrder = async (orderId) => {
    const ok = await confirm({
      title: 'Eliminar pedido',
      description: '¿Eliminar este pedido de forma permanente?',
      confirmText: 'Eliminar',
    });
    if (!ok) return;
    try {
      await axios.delete(`${API}/admin/orders/${orderId}`, getAuth());
      toast.success('Pedido eliminado');
      fetchData();
    } catch { toast.error('Error al eliminar'); }
  };

  const handleUpdateOrderStatus = async (orderId, fulfillment_status, extra = {}) => {
    try {
      await axios.put(`${API}/admin/orders/${orderId}/status`, { fulfillment_status, ...extra }, getAuth());
      toast.success(`Pedido actualizado: ${fulfillment_status}`);
      fetchData();
    } catch (e) {
      const msg = e?.response?.data?.detail || 'Error';
      toast.error(typeof msg === 'string' ? msg : 'Error');
    }
  };

  const handleResolveSubstitution = async (orderId, note = '') => {
    try {
      await axios.post(`${API}/admin/orders/${orderId}/substitution/resolve`, { resolved_note: note }, getAuth());
      toast.success('Sustitución resuelta');
      fetchData();
    } catch { toast.error('Error al resolver'); }
  };

  const handleResendEmail = async (orderId) => {
    try {
      const r = await axios.post(`${API}/admin/orders/${orderId}/resend-email`, {}, getAuth());
      toast.success(`Email reenviado a ${r.data.email}`);
    } catch (e) {
      const msg = e?.response?.data?.detail || 'Error';
      toast.error(typeof msg === 'string' ? msg : 'Error');
    }
  };

  const handleProcessSubstitutionAuto = async (orderId) => {
    try {
      const res = await axios.post(`${API}/admin/orders/${orderId}/substitution/process`, {}, getAuth());
      const d = res.data;
      if (d.status === 'refunded') toast.success(`Reembolso PayPal procesado: $${d.refund_amount_usd} USD`);
      else if (d.status === 'charge_link_created') toast.success(`Link de cobro enviado al cliente: $${d.extra_amount_usd} USD`);
      else if (d.status === 'no_payment_adjustment') toast.success('Sin ajuste de pago necesario');
      fetchData();
    } catch (e) {
      const msg = e?.response?.data?.detail || 'Error al procesar';
      toast.error(typeof msg === 'string' ? msg : 'Error');
    }
  };

  const handleToggleAvailable = async (item) => {
    try {
      await axios.put(`${API}/admin/menu/${item.id}`, {
        ...item,
        available: !item.available
      }, getAuth());
      fetchData();
    } catch {
      toast.error('Error al actualizar');
    }
  };

  const handleSaveSettings = async () => {
    setSavingSettings(true);
    try {
      const { id, updated_at, smtp_password, ...updateData } = settings;
      await axios.put(`${API}/admin/settings`, updateData, getAuth());
      toast.success('Configuracion guardada');
    } catch {
      toast.error('Error al guardar');
    } finally {
      setSavingSettings(false);
    }
  };

  const handleSaveShabbat = async () => {
    setSavingShabbat(true);
    try {
      const { id, ...updateData } = shabbatData;
      await axios.put(`${API}/admin/shabbat/schedule`, updateData, getAuth());
      toast.success('Horarios actualizados');
    } catch {
      toast.error('Error al guardar');
    } finally {
      setSavingShabbat(false);
    }
  };

  const handleSaveMenuItem = async (formData) => {
    try {
      if (editingItem) {
        await axios.put(`${API}/admin/menu/${editingItem.id}`, formData, getAuth());
        toast.success('Platillo actualizado');
      } else {
        await axios.post(`${API}/admin/menu`, formData, getAuth());
        toast.success('Platillo creado');
      }
      setEditingItem(null);
      setShowAddForm(false);
      fetchData();
    } catch {
      toast.error('Error al guardar');
    }
  };

  // Login Screen
  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-[#FAF9F6] pt-20 flex items-center justify-center">
        <Card className="w-full max-w-sm mx-4 bg-white border-[#E5E3D8] rounded-2xl" data-testid="admin-login-card">
          <CardHeader className="text-center">
            <div className="w-16 h-16 rounded-2xl bg-[#0F2922] flex items-center justify-center mx-auto mb-4">
              <Lock className="w-8 h-8 text-white" />
            </div>
            <CardTitle className="font-heading text-[#0F2922]">{t('adminPanel')}</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label className="text-[#0F2922] font-body">Usuario</Label>
              <Input
                value={credentials.username}
                onChange={(e) => setCredentials(p => ({ ...p, username: e.target.value }))}
                className="mt-1 border-[#E5E3D8] rounded-xl"
                data-testid="admin-username"
              />
            </div>
            <div>
              <Label className="text-[#0F2922] font-body">Contraseña</Label>
              <Input
                type="password"
                value={credentials.password}
                onChange={(e) => setCredentials(p => ({ ...p, password: e.target.value }))}
                onKeyDown={(e) => e.key === 'Enter' && handleLogin()}
                className="mt-1 border-[#E5E3D8] rounded-xl"
                data-testid="admin-password"
              />
            </div>
            <Button
              className="w-full bg-[#0F2922] hover:bg-[#0F2922]/90 text-white rounded-xl"
              onClick={handleLogin}
              data-testid="admin-login-button"
            >
              Entrar
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-[#FAF9F6] flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-[#B85042]" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FAF9F6] pt-20 pb-12">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between mb-8">
          <h1 className="font-heading text-3xl font-bold text-[#0F2922]" data-testid="admin-title">
            {t('adminPanel')}
          </h1>
          <NotificationBell
            authHeader={authHeader}
            onClick={() => setActiveTab('orders')}
          />
        </div>

        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
          <TabsList className="bg-white border border-[#E5E3D8] rounded-xl p-1 flex-wrap h-auto">
            <TabsTrigger value="dashboard" className="rounded-lg" data-testid="tab-dashboard">Dashboard</TabsTrigger>
            <TabsTrigger value="menu" className="rounded-lg" data-testid="tab-menu">{t('menuManagement')}</TabsTrigger>
            <TabsTrigger value="shabbat" className="rounded-lg" data-testid="tab-shabbat">{t('shabbatManagement')}</TabsTrigger>
            <TabsTrigger value="orders" className="rounded-lg" data-testid="tab-orders">{t('ordersManagement')}</TabsTrigger>
            <TabsTrigger value="guests" className="rounded-lg" data-testid="tab-guests">Invitados Shabbat</TabsTrigger>
            <TabsTrigger value="hotels" className="rounded-lg" data-testid="tab-hotels">Hoteles</TabsTrigger>
            <TabsTrigger value="settings" className="rounded-lg" data-testid="tab-settings">Configuracion</TabsTrigger>
          </TabsList>

          {/* ===== DASHBOARD TAB ===== */}
          <TabsContent value="dashboard">
            <Dashboard authHeader={authHeader} />
          </TabsContent>

          {/* ===== MENU TAB ===== */}
          <TabsContent value="menu">
            <div className="flex justify-between items-center mb-4">
              <h2 className="font-heading text-xl font-bold text-[#0F2922]">{t('menuManagement')}</h2>
              <Button
                className="bg-[#B85042] hover:bg-[#B85042]/90 text-white rounded-xl"
                onClick={() => { setShowAddForm(true); setEditingItem(null); }}
                data-testid="add-menu-item-button"
              >
                <Plus className="w-4 h-4 mr-2" /> {t('addNew')}
              </Button>
            </div>

            {/* Menu Item Form */}
            {(showAddForm || editingItem) && (
              <MenuItemForm
                key={editingItem?.id || 'new'}
                item={editingItem}
                onSave={handleSaveMenuItem}
                onCancel={() => { setShowAddForm(false); setEditingItem(null); }}
              />
            )}

            {/* Menu Items List */}
            <div className="space-y-3">
              {menuItems.map((item) => (
                <Card key={item.id} className={`bg-white border-[#E5E3D8] rounded-xl ${!item.available ? 'opacity-60' : ''}`}>
                  <CardContent className="p-4 flex items-center gap-4">
                    {item.image_url && (
                      <img src={item.image_url} alt="" className="w-16 h-16 rounded-lg object-cover" />
                    )}
                    <div className="flex-1 min-w-0">
                      <h3 className="font-heading font-bold text-[#0F2922] truncate">{item.name_es}</h3>
                      <p className="text-sm text-[#5C6C62]">${item.price.toFixed(2)} MXN - {item.category}</p>
                    </div>
                    <div className="flex items-center gap-3">
                      <Switch
                        checked={item.available}
                        onCheckedChange={() => handleToggleAvailable(item)}
                        data-testid={`toggle-${item.id}`}
                      />
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => { setEditingItem(item); setShowAddForm(false); }}
                        data-testid={`edit-${item.id}`}
                      >
                        {t('edit')}
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="text-red-500"
                        onClick={() => handleDeleteItem(item.id)}
                        data-testid={`delete-${item.id}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </TabsContent>

          {/* ===== SHABBAT TAB ===== */}
          <TabsContent value="shabbat">
            <Card className="bg-white border-[#E5E3D8] rounded-2xl">
              <CardHeader>
                <CardTitle className="font-heading text-[#0F2922]">{t('shabbatManagement')}</CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Fecha */}
                <div>
                  <h3 className="font-heading font-bold text-[#0F2922] mb-3 text-lg">Fecha</h3>
                  <Input value={shabbatData.date || ''} onChange={(e) => setShabbatData(p => ({ ...p, date: e.target.value }))} className="border-[#E5E3D8] rounded-xl" placeholder="Ej: 25-26 Abril 2026" data-testid="shabbat-date" />
                </div>

                {/* Encendido y Havdalá */}
                <div className="border-t border-[#E5E3D8] pt-4">
                  <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
                    <h3 className="font-heading font-bold text-[#0F2922] text-lg">Encendido de Velas y Havdala</h3>
                    <Button
                      type="button"
                      size="sm"
                      variant="outline"
                      onClick={handleSyncShabbatTimes}
                      disabled={syncingShabbat}
                      className="border-[#B85042] text-[#B85042] rounded-xl"
                      data-testid="btn-sync-shabbat-times"
                    >
                      {syncingShabbat ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                      Sincronizar desde Chabad/Hebcal
                    </Button>
                  </div>
                  <p className="text-xs text-[#6B6660] mb-3">
                    Se sincroniza automáticamente cada domingo. Click para forzar ahora.
                  </p>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <Label className="text-[#5C6C62] text-sm">Encendido de velas</Label>
                      <Input value={shabbatData.candle_lighting || ''} onChange={(e) => setShabbatData(p => ({ ...p, candle_lighting: e.target.value }))} className="mt-1 border-[#E5E3D8] rounded-xl" placeholder="18:30" />
                    </div>
                    <div>
                      <Label className="text-[#5C6C62] text-sm">Havdala</Label>
                      <Input value={shabbatData.havdalah || ''} onChange={(e) => setShabbatData(p => ({ ...p, havdalah: e.target.value }))} className="mt-1 border-[#E5E3D8] rounded-xl" placeholder="19:30" />
                    </div>
                  </div>
                </div>

                {/* Rezos */}
                <div className="border-t border-[#E5E3D8] pt-4">
                  <h3 className="font-heading font-bold text-[#0F2922] mb-3 text-lg">Rezos</h3>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                      <Label className="text-[#5C6C62] text-sm">Arvit</Label>
                      <Input value={shabbatData.arvit || ''} onChange={(e) => setShabbatData(p => ({ ...p, arvit: e.target.value }))} className="mt-1 border-[#E5E3D8] rounded-xl" placeholder="19:00" data-testid="shabbat-arvit" />
                    </div>
                    <div>
                      <Label className="text-[#5C6C62] text-sm">Shajarit</Label>
                      <Input value={shabbatData.shacharit || ''} onChange={(e) => setShabbatData(p => ({ ...p, shacharit: e.target.value }))} className="mt-1 border-[#E5E3D8] rounded-xl" placeholder="09:00" />
                    </div>
                    <div>
                      <Label className="text-[#5C6C62] text-sm">Minja</Label>
                      <Input value={shabbatData.mincha || ''} onChange={(e) => setShabbatData(p => ({ ...p, mincha: e.target.value }))} className="mt-1 border-[#E5E3D8] rounded-xl" placeholder="18:00" />
                    </div>
                  </div>
                </div>

                {/* Comidas */}
                <div className="border-t border-[#E5E3D8] pt-4">
                  <h3 className="font-heading font-bold text-[#0F2922] mb-3 text-lg">Comidas</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="p-4 bg-[#FAF9F6] rounded-xl space-y-3">
                      <p className="font-heading font-bold text-[#0F2922]">Comida de noche de Shabbat (Viernes)</p>
                      <div>
                        <Label className="text-[#5C6C62] text-sm">Hora</Label>
                        <Input value={shabbatData.friday_dinner_time || ''} onChange={(e) => setShabbatData(p => ({ ...p, friday_dinner_time: e.target.value }))} className="mt-1 border-[#E5E3D8] rounded-xl" placeholder="19:30" />
                      </div>
                      <div className="grid grid-cols-2 gap-2">
                        <div>
                          <Label className="text-[#5C6C62] text-sm">Precio MXN</Label>
                          <Input type="number" value={shabbatData.friday_dinner_cost || ''} onChange={(e) => setShabbatData(p => ({ ...p, friday_dinner_cost: parseFloat(e.target.value) || 0 }))} className="mt-1 border-[#E5E3D8] rounded-xl" placeholder="250" />
                        </div>
                        <div>
                          <Label className="text-[#5C6C62] text-sm">Precio USD</Label>
                          <Input type="number" value={shabbatData.friday_dinner_cost_usd || ''} onChange={(e) => setShabbatData(p => ({ ...p, friday_dinner_cost_usd: parseFloat(e.target.value) || 0 }))} className="mt-1 border-[#E5E3D8] rounded-xl" placeholder="15" />
                        </div>
                      </div>
                    </div>
                    <div className="p-4 bg-[#FAF9F6] rounded-xl space-y-3">
                      <p className="font-heading font-bold text-[#0F2922]">Comida de día de Shabbat (Sabado)</p>
                      <div>
                        <Label className="text-[#5C6C62] text-sm">Hora</Label>
                        <Input value={shabbatData.saturday_lunch_time || ''} onChange={(e) => setShabbatData(p => ({ ...p, saturday_lunch_time: e.target.value }))} className="mt-1 border-[#E5E3D8] rounded-xl" placeholder="12:30" />
                      </div>
                      <div className="grid grid-cols-2 gap-2">
                        <div>
                          <Label className="text-[#5C6C62] text-sm">Precio MXN</Label>
                          <Input type="number" value={shabbatData.saturday_lunch_cost || ''} onChange={(e) => setShabbatData(p => ({ ...p, saturday_lunch_cost: parseFloat(e.target.value) || 0 }))} className="mt-1 border-[#E5E3D8] rounded-xl" placeholder="200" />
                        </div>
                        <div>
                          <Label className="text-[#5C6C62] text-sm">Precio USD</Label>
                          <Input type="number" value={shabbatData.saturday_lunch_cost_usd || ''} onChange={(e) => setShabbatData(p => ({ ...p, saturday_lunch_cost_usd: parseFloat(e.target.value) || 0 }))} className="mt-1 border-[#E5E3D8] rounded-xl" placeholder="12" />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Instrucciones */}
                <div className="border-t border-[#E5E3D8] pt-4">
                  <h3 className="font-heading font-bold text-[#0F2922] mb-3 text-lg">Instrucciones</h3>
                  <div className="space-y-3">
                    <div>
                      <Label className="text-[#5C6C62] text-sm">Instrucciones (Espanol)</Label>
                      <Textarea value={shabbatData.instructions_es || ''} onChange={(e) => setShabbatData(p => ({ ...p, instructions_es: e.target.value }))} className="mt-1 border-[#E5E3D8] rounded-xl" rows={3} />
                    </div>
                    <div>
                      <Label className="text-[#5C6C62] text-sm">Instructions (English)</Label>
                      <Textarea value={shabbatData.instructions_en || ''} onChange={(e) => setShabbatData(p => ({ ...p, instructions_en: e.target.value }))} className="mt-1 border-[#E5E3D8] rounded-xl" rows={3} />
                    </div>
                    <div>
                      <Label className="text-[#5C6C62] text-sm">הוראות (עברית)</Label>
                      <Textarea value={shabbatData.instructions_he || ''} onChange={(e) => setShabbatData(p => ({ ...p, instructions_he: e.target.value }))} className="mt-1 border-[#E5E3D8] rounded-xl" rows={3} dir="rtl" />
                    </div>
                  </div>
                </div>

                <Button
                  className="bg-[#B85042] hover:bg-[#B85042]/90 text-white rounded-xl"
                  onClick={handleSaveShabbat}
                  disabled={savingShabbat}
                  data-testid="save-shabbat-button"
                >
                  {savingShabbat ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                  {t('save')}
                </Button>
              </CardContent>
            </Card>
          </TabsContent>

          {/* ===== ORDERS TAB ===== */}
          <TabsContent value="orders">
            <div className="flex flex-wrap items-center justify-between gap-2 mb-4">
              <h2 className="font-heading text-xl font-bold text-[#0F2922]">{t('ordersManagement')}</h2>
              <Button
                size="sm"
                variant="outline"
                className="border-red-200 text-red-700 rounded-lg"
                onClick={async () => {
                  const ok = await confirm({
                    title: 'Borrar pedidos entregados',
                    description: '¿Eliminar TODOS los pedidos entregados? No se puede deshacer.',
                    confirmText: 'Borrar todos',
                  });
                  if (!ok) return;
                  try {
                    const res = await axios.delete(`${API}/admin/orders?fulfillment_status=delivered`, getAuth());
                    toast.success(`${res.data.count} pedidos eliminados`);
                    fetchData();
                  } catch { toast.error('Error en eliminación masiva'); }
                }}
                data-testid="btn-bulk-delete-delivered"
              >
                <Trash2 className="w-3.5 h-3.5 mr-1" /> Borrar entregados
              </Button>
            </div>
            {orders.length === 0 ? (
              <Card className="bg-white border-[#E5E3D8] rounded-2xl">
                <CardContent className="p-8 text-center">
                  <ShoppingBag className="w-12 h-12 text-[#E5E3D8] mx-auto mb-4" />
                  <p className="text-[#5C6C62]">No hay pedidos todavia</p>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-3">
                {orders.map((order) => (
                  <Card key={order.id} className="bg-white border-[#E5E3D8] rounded-xl">
                    <CardContent className="p-4">
                      <div className="flex flex-wrap items-start justify-between gap-2 mb-3">
                        <div>
                          <h3 className="font-heading font-bold text-[#0F2922]">
                            #{order.id?.slice(0, 8)} - {order.customer_name}
                          </h3>
                          <p className="text-sm text-[#5C6C62]">{order.customer_phone}</p>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                            order.payment_status === 'paid' ? 'bg-green-100 text-green-700' :
                            order.payment_status === 'pending' ? 'bg-yellow-100 text-yellow-700' :
                            'bg-red-100 text-red-700'
                          }`}>
                            {order.payment_status}
                          </span>
                          <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                            order.is_pickup ? 'bg-blue-100 text-blue-700' : 'bg-purple-100 text-purple-700'
                          }`}>
                            {order.is_pickup ? 'Pickup' : `Delivery - ${order.delivery_zone}`}
                          </span>
                        </div>
                      </div>
                      <div className="text-sm text-[#5C6C62] space-y-1">
                        {order.items?.map((item, i) => (
                          <p key={i}>{item.quantity}x {item.name_es} - ${item.subtotal?.toFixed(2)}</p>
                        ))}
                      </div>
                      <div className="mt-2 pt-2 border-t border-[#E5E3D8] flex flex-wrap items-center justify-between gap-2">
                        <span className="text-sm text-[#5C6C62]">
                          {order.delivery_address}
                        </span>
                        <span className="font-heading font-bold text-[#B85042]">
                          ${order.total_amount?.toFixed(2)} MXN
                        </span>
                      </div>
                      {/* Order Fulfillment Status Actions */}
                      <div className="mt-3 pt-2 border-t border-[#E5E3D8] space-y-2">
                        {(() => {
                          const f = order.fulfillment_status || 'received';
                          const colors = {
                            received: 'bg-blue-100 text-blue-700',
                            in_process: 'bg-yellow-100 text-yellow-700',
                            out_for_delivery: 'bg-orange-100 text-orange-700',
                            delivered: 'bg-green-100 text-green-700',
                            substitution_needed: 'bg-red-100 text-red-700'
                          };
                          const labels = {
                            received: '🟢 Recibido',
                            in_process: '🟡 En proceso',
                            out_for_delivery: '🚚 En entrega',
                            delivered: '✅ Entregado',
                            substitution_needed: '⚠️ Requiere sustitución'
                          };
                          return <span className={`px-3 py-1 rounded-full text-xs font-medium ${colors[f]}`} data-testid={`order-status-${order.id}`}>{labels[f]}</span>;
                        })()}
                        <div className="flex flex-wrap gap-1">
                          {order.fulfillment_status !== 'received' && <Button size="sm" variant="ghost" className="text-xs h-7 px-2" onClick={() => handleUpdateOrderStatus(order.id, 'received')} data-testid={`btn-received-${order.id}`}>Recibido</Button>}
                          {order.fulfillment_status !== 'in_process' && <Button size="sm" variant="ghost" className="text-xs h-7 px-2" onClick={() => handleUpdateOrderStatus(order.id, 'in_process')} data-testid={`btn-process-${order.id}`}>En proceso</Button>}
                          {order.fulfillment_status !== 'out_for_delivery' && !order.is_pickup && <Button size="sm" variant="ghost" className="text-xs h-7 px-2" onClick={() => handleUpdateOrderStatus(order.id, 'out_for_delivery')} data-testid={`btn-delivery-${order.id}`}>En entrega</Button>}
                          {order.fulfillment_status !== 'delivered' && <Button size="sm" className="text-xs h-7 px-2 bg-[#25D366] hover:bg-[#25D366]/90 text-white" onClick={() => handleUpdateOrderStatus(order.id, 'delivered')} data-testid={`btn-delivered-${order.id}`}>Entregado</Button>}
                          <Button size="sm" variant="outline" className="text-xs h-7 px-2 border-orange-300 text-orange-700 hover:bg-orange-50 ml-auto" onClick={() => setSubstitutionOrder(order)} data-testid={`btn-sub-${order.id}`}>⚠️ Sustitución</Button>
                          {order.customer_email && (
                            <Button size="sm" variant="ghost" className="text-xs h-7 px-2" onClick={() => handleResendEmail(order.id)} data-testid={`btn-resend-${order.id}`} title={`Reenviar email a ${order.customer_email}`}>✉️ Reenviar email</Button>
                          )}
                        </div>
                        {order.substitution && !order.substitution.resolved && (
                          <div className="bg-orange-50 border border-orange-200 rounded-lg p-3 text-xs">
                            <p className="font-bold text-orange-900">Item agotado: {order.substitution.item_name}</p>
                            {order.substitution.customer_choice === 'pending' ? (
                              <p className="text-orange-700">Esperando respuesta del cliente...</p>
                            ) : (
                              <>
                                <p className="text-orange-700 mt-1">
                                  Cliente eligió: <b>{order.substitution.customer_choice === 'refund' ? 'Reembolso' : `Reemplazar por ${order.substitution.replacement_item_name_es} x${order.substitution.replacement_quantity}`}</b>
                                </p>
                                {order.substitution.diff_action === 'charge_extra' && <p className="text-orange-700">Cobrar extra: ${order.substitution.diff_mxn?.toFixed(2)} MXN</p>}
                                {order.substitution.diff_action === 'refund_difference' && <p className="text-orange-700">Reembolsar: ${Math.abs(order.substitution.diff_mxn || 0).toFixed(2)} MXN</p>}
                                {order.substitution.diff_action === 'full_refund_item' && <p className="text-orange-700">Reembolsar item: ${order.substitution.refund_amount_mxn?.toFixed(2)} MXN</p>}
                                <div className="flex gap-2 mt-2">
                                  {!order.substitution.auto_processed && (
                                    <Button size="sm" className="text-xs h-7 bg-[#0070BA] hover:bg-[#0070BA]/90 text-white" onClick={() => handleProcessSubstitutionAuto(order.id)} data-testid={`btn-auto-${order.id}`}>⚡ Procesar PayPal auto</Button>
                                  )}
                                  {order.substitution.auto_processed && order.substitution.auto_result?.status === 'charge_link_created' && (
                                    <span className="text-xs text-blue-700">Esperando pago del cliente...</span>
                                  )}
                                  <Button size="sm" variant="outline" className="text-xs h-7 border-orange-400 text-orange-700" onClick={() => handleResolveSubstitution(order.id)} data-testid={`btn-resolve-${order.id}`}>Marcar resuelto</Button>
                                </div>
                              </>
                            )}
                          </div>
                        )}

                        {/* Manual transfer & danger actions */}
                        <div className="flex flex-wrap gap-2 mt-3 pt-3 border-t border-[#E5E3D8]">
                          {order.payment_status !== 'paid' && (
                            <Button
                              size="sm"
                              className="text-xs h-7 bg-[#25D366] hover:bg-[#25D366]/90 text-white rounded-lg"
                              onClick={async () => {
                                try {
                                  await axios.post(`${API}/admin/orders/${order.id}/mark-paid`, null, getAuth());
                                  toast.success('Marcado como pagado · email enviado');
                                  fetchData();
                                } catch { toast.error('Error'); }
                              }}
                              data-testid={`btn-mark-paid-${order.id}`}
                            >
                              Marcar pagado (transferencia)
                            </Button>
                          )}
                          <Button
                            size="sm"
                            variant="ghost"
                            className="text-xs h-7 text-red-700 hover:bg-red-50 rounded-lg ml-auto"
                            onClick={() => handleDeleteOrder(order.id)}
                            data-testid={`btn-delete-order-${order.id}`}
                          >
                            <Trash2 className="w-3.5 h-3.5 mr-1" /> Eliminar pedido
                          </Button>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>

          {/* ===== SHABBAT GUESTS TAB ===== */}
          <TabsContent value="guests">
            <div className="flex flex-wrap items-center justify-between gap-2 mb-4">
              <h2 className="font-heading text-xl font-bold text-[#0F2922]">Invitados Shabbat</h2>
              <div className="flex gap-2 flex-wrap">
                <Button size="sm" variant="outline" className="border-red-200 text-red-700 rounded-lg" onClick={() => handleBulkDeleteGuests('approved')} data-testid="btn-bulk-delete-approved">
                  <Trash2 className="w-3.5 h-3.5 mr-1" /> Borrar todos aprobados
                </Button>
                <Button size="sm" variant="outline" className="border-red-200 text-red-700 rounded-lg" onClick={() => handleBulkDeleteGuests('rejected')} data-testid="btn-bulk-delete-rejected">
                  <Trash2 className="w-3.5 h-3.5 mr-1" /> Borrar todos rechazados
                </Button>
              </div>
            </div>

            {shabbatGuestsSummary && (
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-5" data-testid="guests-summary">
                <Card className="bg-white border-[#E5E3D8] rounded-xl">
                  <CardContent className="p-4">
                    <div className="text-xs uppercase tracking-wider text-[#6B6660]">Aprobados · Comida de noche de Shabbat</div>
                    <div className="text-3xl font-bold text-[#0F2922] mt-1" data-testid="summary-friday">{shabbatGuestsSummary.friday_dinner.guests}</div>
                    <div className="text-xs text-[#6B6660]">{shabbatGuestsSummary.friday_dinner.registrations} reservación(es)</div>
                  </CardContent>
                </Card>
                <Card className="bg-white border-[#E5E3D8] rounded-xl">
                  <CardContent className="p-4">
                    <div className="text-xs uppercase tracking-wider text-[#6B6660]">Aprobados · Comida de día de Shabbat</div>
                    <div className="text-3xl font-bold text-[#0F2922] mt-1" data-testid="summary-saturday">{shabbatGuestsSummary.saturday_lunch.guests}</div>
                    <div className="text-xs text-[#6B6660]">{shabbatGuestsSummary.saturday_lunch.registrations} reservación(es)</div>
                  </CardContent>
                </Card>
                <Card className="bg-white border-[#E5E3D8] rounded-xl">
                  <CardContent className="p-4">
                    <div className="text-xs uppercase tracking-wider text-[#6B6660]">Aprobados · Solo rezar</div>
                    <div className="text-3xl font-bold text-[#0F2922] mt-1" data-testid="summary-prayer">{shabbatGuestsSummary.prayer_only.guests}</div>
                    <div className="text-xs text-[#6B6660]">{shabbatGuestsSummary.prayer_only.registrations} reservación(es)</div>
                  </CardContent>
                </Card>
              </div>
            )}

            {shabbatGuests.length === 0 ? (
              <Card className="bg-white border-[#E5E3D8] rounded-2xl">
                <CardContent className="p-8 text-center">
                  <p className="text-[#5C6C62]">No hay solicitudes todavia</p>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-3">
                {shabbatGuests.map((guest) => (
                  <Card key={guest.id} className="bg-white border-[#E5E3D8] rounded-xl">
                    <CardContent className="p-4">
                      <div className="flex flex-wrap items-start justify-between gap-2 mb-2">
                        <div>
                          <h3 className="font-heading font-bold text-[#0F2922]">{guest.name}</h3>
                          <p className="text-sm text-[#5C6C62]">{guest.email} | {guest.phone}</p>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                            guest.status === 'approved' ? 'bg-green-100 text-green-700' :
                            guest.status === 'rejected' ? 'bg-red-100 text-red-700' :
                            'bg-yellow-100 text-yellow-700'
                          }`}>{guest.status}</span>
                          <span className={`px-3 py-1 rounded-full text-xs font-medium ${guest.has_paid ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'}`}>
                            {guest.has_paid ? 'Pagado' : 'Sin pago'}
                          </span>
                        </div>
                      </div>
                      <div className="text-sm text-[#5C6C62] mb-3">
                        <span>Comida: {guest.meal_type === 'friday_dinner' ? 'Noche de Shabbat' : guest.meal_type === 'saturday_lunch' ? 'Día de Shabbat' : guest.meal_type === 'both' ? 'Ambas' : (guest.meal_type === 'prayers_only' || guest.meal_type === 'prayer_only') ? 'Solo rezos' : guest.meal_type}</span>
                        <span className="ml-4">Invitados: {guest.guests_count}</span>
                        {guest.notes && <p className="mt-1">Notas: {guest.notes}</p>}
                      </div>
                      {guest.status === 'pending' && (
                        <div className="flex gap-2 flex-wrap">
                          <Button size="sm" className="bg-[#25D366] hover:bg-[#25D366]/90 text-white rounded-lg" onClick={() => handleApproveGuest(guest.id, 'approved')} data-testid={`approve-${guest.id}`}>
                            Aprobar
                          </Button>
                          <Button size="sm" variant="outline" className="border-red-300 text-red-600 rounded-lg" onClick={() => handleApproveGuest(guest.id, 'rejected')} data-testid={`reject-${guest.id}`}>
                            Rechazar
                          </Button>
                          <Button size="sm" variant="ghost" className="text-red-700 hover:bg-red-50 rounded-lg" onClick={() => handleDeleteGuest(guest.id)} data-testid={`delete-guest-${guest.id}`}>
                            <Trash2 className="w-3.5 h-3.5 mr-1" /> Eliminar
                          </Button>
                        </div>
                      )}
                      {guest.status !== 'pending' && (
                        <div className="flex">
                          <Button size="sm" variant="ghost" className="text-red-700 hover:bg-red-50 rounded-lg" onClick={() => handleDeleteGuest(guest.id)} data-testid={`delete-guest-${guest.id}`}>
                            <Trash2 className="w-3.5 h-3.5 mr-1" /> Eliminar registro
                          </Button>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>

          {/* ===== HOTELS TAB ===== */}
          <TabsContent value="hotels">
            <div className="flex justify-between items-center mb-4">
              <h2 className="font-heading text-xl font-bold text-[#0F2922]">Hoteles Recomendados</h2>
              <Button className="bg-[#B85042] hover:bg-[#B85042]/90 text-white rounded-xl" onClick={() => { setShowAddHotel(true); setEditingHotel(null); setHotelForm({ name: '', url: '', image_url: '', order: hotels.length + 1 }); }} data-testid="add-hotel-button">
                <Plus className="w-4 h-4 mr-2" /> Agregar hotel
              </Button>
            </div>

            {(showAddHotel || editingHotel) && (
              <Card className="bg-white border-[#E5E3D8] rounded-2xl mb-6">
                <CardContent className="p-4 space-y-3">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <div>
                      <Label className="text-xs text-[#5C6C62]">Nombre del hotel</Label>
                      <Input value={hotelForm.name} onChange={(e) => setHotelForm(p => ({ ...p, name: e.target.value }))} className="border-[#E5E3D8] rounded-xl" />
                    </div>
                    <div>
                      <Label className="text-xs text-[#5C6C62]">URL / Link</Label>
                      <Input value={hotelForm.url} onChange={(e) => setHotelForm(p => ({ ...p, url: e.target.value }))} className="border-[#E5E3D8] rounded-xl" placeholder="https://..." />
                    </div>
                    <div>
                      <Label className="text-xs text-[#5C6C62]">URL de imagen</Label>
                      <Input value={hotelForm.image_url} onChange={(e) => setHotelForm(p => ({ ...p, image_url: e.target.value }))} className="border-[#E5E3D8] rounded-xl" />
                    </div>
                    <div>
                      <Label className="text-xs text-[#5C6C62]">Orden</Label>
                      <Input type="number" value={hotelForm.order} onChange={(e) => setHotelForm(p => ({ ...p, order: parseInt(e.target.value) || 0 }))} className="border-[#E5E3D8] rounded-xl" />
                    </div>
                  </div>
                  <div className="flex gap-3">
                    <Button variant="outline" className="border-[#E5E3D8] rounded-xl" onClick={() => { setShowAddHotel(false); setEditingHotel(null); }}>Cancelar</Button>
                    <Button className="bg-[#B85042] hover:bg-[#B85042]/90 text-white rounded-xl" onClick={handleSaveHotel}>
                      <Save className="w-4 h-4 mr-2" /> Guardar
                    </Button>
                  </div>
                </CardContent>
              </Card>
            )}

            <div className="space-y-3">
              {hotels.sort((a, b) => (a.order || 0) - (b.order || 0)).map((hotel) => (
                <Card key={hotel.id} className="bg-white border-[#E5E3D8] rounded-xl">
                  <CardContent className="p-4 flex items-center gap-4">
                    {hotel.image_url && <img src={hotel.image_url} alt="" className="w-16 h-16 rounded-lg object-cover" />}
                    <div className="flex-1 min-w-0">
                      <h3 className="font-heading font-bold text-[#0F2922] truncate">{hotel.name}</h3>
                      <p className="text-xs text-[#5C6C62] truncate">{hotel.url}</p>
                    </div>
                    <div className="flex items-center gap-2">
                      <Button variant="ghost" size="sm" onClick={() => { setEditingHotel(hotel); setShowAddHotel(false); setHotelForm({ name: hotel.name, url: hotel.url, image_url: hotel.image_url || '', order: hotel.order || 0 }); }}>
                        {t('edit')}
                      </Button>
                      <Button variant="ghost" size="icon" className="text-red-500" onClick={() => handleDeleteHotel(hotel.id)}>
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </TabsContent>

          {/* ===== SETTINGS TAB ===== */}
          <TabsContent value="settings">
            <Card className="bg-white border-[#E5E3D8] rounded-2xl">
              <CardHeader>
                <CardTitle className="font-heading text-[#0F2922] flex items-center gap-2">
                  <Settings className="w-5 h-5" /> Configuracion General
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Pickup Address */}
                <div>
                  <h3 className="font-heading font-bold text-[#0F2922] mb-3">Direccion de Recogida (Pickup)</h3>
                  <Input
                    value={settings.pickup_address || ''}
                    onChange={(e) => setSettings(p => ({ ...p, pickup_address: e.target.value }))}
                    className="border-[#E5E3D8] rounded-xl"
                    placeholder="Av. Mexico 2500, Col. Ladron de Guevara..."
                    data-testid="settings-pickup-address"
                  />
                </div>

                {/* WhatsApp Numbers */}
                <div>
                  <h3 className="font-heading font-bold text-[#0F2922] mb-3">Numeros de WhatsApp</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <Label className="text-[#5C6C62] text-sm">WhatsApp Pedidos (sin +)</Label>
                      <Input
                        value={settings.whatsapp_orders || ''}
                        onChange={(e) => setSettings(p => ({ ...p, whatsapp_orders: e.target.value }))}
                        className="mt-1 border-[#E5E3D8] rounded-xl"
                        placeholder="523331463068"
                        data-testid="settings-wa-orders"
                      />
                      <p className="text-[10px] text-[#5C6C62] mt-1">Numero que recibira notificaciones automaticas de pedidos.</p>
                    </div>
                    <div>
                      <Label className="text-[#5C6C62] text-sm">WhatsApp Contacto (sin +)</Label>
                      <Input
                        value={settings.whatsapp_contact || ''}
                        onChange={(e) => setSettings(p => ({ ...p, whatsapp_contact: e.target.value }))}
                        className="mt-1 border-[#E5E3D8] rounded-xl"
                        placeholder="523338140770"
                        data-testid="settings-wa-contact"
                      />
                    </div>
                  </div>
                  <div className="mt-4">
                    <Label className="text-[#5C6C62] text-sm">CallMeBot API Key (notificaciones automaticas)</Label>
                    <Input
                      type="password"
                      value={settings.whatsapp_api_key || ''}
                      onChange={(e) => setSettings(p => ({ ...p, whatsapp_api_key: e.target.value }))}
                      className="mt-1 border-[#E5E3D8] rounded-xl font-mono text-sm"
                      placeholder="ej: 1234567"
                      data-testid="settings-wa-api-key"
                    />
                    <p className="text-[10px] text-[#5C6C62] mt-1">
                      Activa CallMeBot: agrega el numero <strong>+34 644 51 95 23</strong> como contacto y envia
                      <strong> &quot;I allow callmebot to send me messages&quot;</strong> desde el WhatsApp de Pedidos. Te respondera con tu API key.
                    </p>
                    <Button
                      type="button"
                      onClick={async () => {
                        try {
                          toast.loading('Enviando mensaje de prueba...', { id: 'wa-test' });
                          const res = await axios.post(`${API}/admin/test-whatsapp`, {}, {
                            headers: { Authorization: authHeader }
                          });
                          toast.dismiss('wa-test');
                          if (res.data.ok) {
                            toast.success('Mensaje enviado! Revisa el WhatsApp ' + (res.data.recipient || ''));
                          } else {
                            const body = res.data.response_body || res.data.error || 'Sin respuesta';
                            toast.error(`Fallo: ${body.slice(0, 200)}`, { duration: 10000 });
                            console.warn('CallMeBot test response:', res.data);
                          }
                        } catch (err) {
                          toast.dismiss('wa-test');
                          toast.error('Error: ' + (err?.message || 'desconocido'));
                        }
                      }}
                      className="mt-2 bg-[#25D366] hover:bg-[#25D366]/90 text-white rounded-xl text-sm h-8"
                      data-testid="test-whatsapp-button"
                    >
                      Probar envio de WhatsApp
                    </Button>
                  </div>
                  <div className="mt-4">
                    <Label className="text-[#5C6C62] text-sm">Email para notificaciones de pedidos</Label>
                    <Input
                      value={settings.notification_email || ''}
                      onChange={(e) => setSettings(p => ({ ...p, notification_email: e.target.value }))}
                      className="mt-1 border-[#E5E3D8] rounded-xl"
                      placeholder="kosherguadalajara@gmail.com"
                      data-testid="settings-notification-email"
                    />
                  </div>
                </div>

                {/* Payment Methods */}
                <div className="border-t border-[#E5E3D8] pt-6">
                  <h3 className="font-heading font-bold text-[#0F2922] mb-3">Métodos de pago</h3>
                  <div className="flex items-start gap-3 p-4 bg-[#FAF9F6] rounded-xl mb-4">
                    <input
                      type="checkbox"
                      id="transfer-toggle"
                      checked={!!settings.transfer_enabled}
                      onChange={(e) => setSettings(p => ({ ...p, transfer_enabled: e.target.checked }))}
                      className="mt-1 w-5 h-5 accent-[#B85042]"
                      data-testid="settings-transfer-toggle"
                    />
                    <label htmlFor="transfer-toggle" className="cursor-pointer flex-1">
                      <div className="font-medium text-[#0F2922]">Aceptar pagos por transferencia bancaria</div>
                      <div className="text-sm text-[#6B6660] mt-1">
                        Cuando esté activado, los clientes verán los datos bancarios como alternativa a PayPal.
                        Tu equipo deberá marcar los pedidos como pagados manualmente.
                      </div>
                    </label>
                  </div>
                  {settings.transfer_enabled && (
                    <div>
                      <Label className="text-[#5C6C62] text-sm">Datos bancarios (se mostrarán al cliente)</Label>
                      <Textarea
                        value={settings.transfer_bank_info || ''}
                        onChange={(e) => setSettings(p => ({ ...p, transfer_bank_info: e.target.value }))}
                        rows={4}
                        className="mt-1 border-[#E5E3D8] rounded-xl font-mono text-sm"
                        placeholder={'BBVA · Jabad Guadalajara A.C.\nCLABE: 012 320 00000000000 0\nReferencia: tu nombre'}
                        data-testid="settings-transfer-bank-info"
                      />
                    </div>
                  )}
                </div>

                {/* PayPal Credentials */}
                <div className="border-t border-[#E5E3D8] pt-6">
                  <h3 className="font-heading font-bold text-[#0F2922] mb-3">PayPal (opcional)</h3>
                  <p className="text-sm text-[#5C6C62] mb-3">Obtener credenciales en: <a href="https://developer.paypal.com" target="_blank" rel="noopener noreferrer" className="text-[#B85042] underline">developer.paypal.com</a></p>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <Label className="text-[#5C6C62] text-sm">PayPal Client ID</Label>
                      <Input
                        value={settings.paypal_client_id || ''}
                        onChange={(e) => setSettings(p => ({ ...p, paypal_client_id: e.target.value }))}
                        className="mt-1 border-[#E5E3D8] rounded-xl"
                        placeholder="Tu Client ID"
                        data-testid="settings-paypal-id"
                      />
                    </div>
                    <div>
                      <Label className="text-[#5C6C62] text-sm">PayPal Secret</Label>
                      <Input
                        type="password"
                        value={settings.paypal_secret || ''}
                        onChange={(e) => setSettings(p => ({ ...p, paypal_secret: e.target.value }))}
                        className="mt-1 border-[#E5E3D8] rounded-xl"
                        placeholder="Tu Secret"
                        data-testid="settings-paypal-secret"
                      />
                    </div>
                  </div>
                </div>

                {/* Email Config */}
                <div className="border-t border-[#E5E3D8] pt-6">
                  <h3 className="font-heading font-bold text-[#0F2922] mb-3">Email para Confirmaciones</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <Label className="text-[#5C6C62] text-sm">Email SMTP</Label>
                      <Input
                        value={settings.smtp_email || ''}
                        onChange={(e) => setSettings(p => ({ ...p, smtp_email: e.target.value }))}
                        className="mt-1 border-[#E5E3D8] rounded-xl"
                        placeholder="tu@gmail.com"
                        data-testid="settings-smtp-email"
                      />
                    </div>
                    <div>
                      <Label className="text-[#5C6C62] text-sm">Estado</Label>
                      <div className="mt-1 p-3 bg-[#FAF9F6] rounded-xl">
                        <span className={`text-sm font-medium ${settings.smtp_email ? 'text-green-600' : 'text-yellow-600'}`}>
                          {settings.smtp_email ? 'Configurado' : 'No configurado'}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Approval/Rejection Messages */}
                <div className="border-t border-[#E5E3D8] pt-6">
                  <h3 className="font-heading font-bold text-[#0F2922] mb-3">Mensajes de Aprobacion/Rechazo Shabbat</h3>
                  <div className="space-y-3">
                    <p className="text-sm font-medium text-[#0F2922]">Mensaje de aprobacion</p>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
                      <div>
                        <Label className="text-xs text-[#5C6C62]">Espanol</Label>
                        <Textarea value={settings.approval_message || 'Nos complace anunciarle que usted fue aprobado para asistir este Shabbat.'} onChange={(e) => setSettings(p => ({ ...p, approval_message: e.target.value }))} className="mt-1 border-[#E5E3D8] rounded-xl" rows={2} />
                      </div>
                      <div>
                        <Label className="text-xs text-[#5C6C62]">English</Label>
                        <Textarea value={settings.approval_message_en || 'We are pleased to announce that you have been approved to attend this Shabbat.'} onChange={(e) => setSettings(p => ({ ...p, approval_message_en: e.target.value }))} className="mt-1 border-[#E5E3D8] rounded-xl" rows={2} />
                      </div>
                      <div>
                        <Label className="text-xs text-[#5C6C62]">עברית</Label>
                        <Textarea value={settings.approval_message_he || 'אנו שמחים להודיע לך שאושרת להשתתף בשבת הקרובה.'} onChange={(e) => setSettings(p => ({ ...p, approval_message_he: e.target.value }))} className="mt-1 border-[#E5E3D8] rounded-xl" rows={2} dir="rtl" />
                      </div>
                    </div>
                    <p className="text-sm font-medium text-[#0F2922] mt-4">Mensaje de rechazo</p>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
                      <div>
                        <Label className="text-xs text-[#5C6C62]">Espanol</Label>
                        <Textarea value={settings.rejection_message || 'Lamentamos informarle que usted no fue aprobado para asistir este Shabbat.'} onChange={(e) => setSettings(p => ({ ...p, rejection_message: e.target.value }))} className="mt-1 border-[#E5E3D8] rounded-xl" rows={2} />
                      </div>
                      <div>
                        <Label className="text-xs text-[#5C6C62]">English</Label>
                        <Textarea value={settings.rejection_message_en || 'We regret to inform you that you have not been approved to attend this Shabbat.'} onChange={(e) => setSettings(p => ({ ...p, rejection_message_en: e.target.value }))} className="mt-1 border-[#E5E3D8] rounded-xl" rows={2} />
                      </div>
                      <div>
                        <Label className="text-xs text-[#5C6C62]">עברית</Label>
                        <Textarea value={settings.rejection_message_he || 'אנו מצטערים להודיע לך שלא אושרת להשתתף בשבת הקרובה.'} onChange={(e) => setSettings(p => ({ ...p, rejection_message_he: e.target.value }))} className="mt-1 border-[#E5E3D8] rounded-xl" rows={2} dir="rtl" />
                      </div>
                    </div>
                  </div>
                </div>

                {/* Delivery Zones */}
                <div className="border-t border-[#E5E3D8] pt-6">
                  <h3 className="font-heading font-bold text-[#0F2922] mb-1">Zonas de Envio y Precios</h3>
                  <p className="text-xs text-[#5C6C62] mb-3">Edita el costo de envio por zona en pesos mexicanos (MXN) y dolares (USD). Recuerda hacer clic en &quot;Guardar Configuracion&quot;.</p>
                  <div className="hidden md:flex items-center gap-4 px-3 pb-1 text-xs font-semibold text-[#5C6C62] uppercase tracking-wider">
                    <span className="w-36">Zona</span>
                    <span className="w-32">Precio MXN</span>
                    <span className="w-32">Precio USD</span>
                  </div>
                  <div className="space-y-3">
                    {Object.entries(settings.delivery_zones || {}).filter(([key]) => key !== 'pickup').map(([key, zone]) => (
                      <div key={key} className="flex flex-wrap items-center gap-4 p-3 bg-[#FAF9F6] rounded-xl">
                        <span className="font-body font-medium text-[#0F2922] w-36">{zone.name_es || key}</span>
                        <div className="flex items-center gap-2">
                          <span className="text-xs text-[#5C6C62]">$</span>
                          <Input
                            type="number"
                            value={zone.price ?? ''}
                            onChange={(e) => {
                              setSettings(prev => {
                                const newZones = JSON.parse(JSON.stringify(prev.delivery_zones));
                                newZones[key] = { ...newZones[key], price: parseFloat(e.target.value) || 0 };
                                return { ...prev, delivery_zones: newZones };
                              });
                            }}
                            className="w-24 border-[#E5E3D8] rounded-xl"
                            data-testid={`zone-price-${key}`}
                          />
                          <span className="text-xs text-[#5C6C62]">MXN</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className="text-xs text-[#5C6C62]">$</span>
                          <Input
                            type="number"
                            value={zone.price_usd ?? ''}
                            onChange={(e) => {
                              setSettings(prev => {
                                const newZones = JSON.parse(JSON.stringify(prev.delivery_zones));
                                newZones[key] = { ...newZones[key], price_usd: parseFloat(e.target.value) || 0 };
                                return { ...prev, delivery_zones: newZones };
                              });
                            }}
                            className="w-24 border-[#E5E3D8] rounded-xl"
                            data-testid={`zone-price-usd-${key}`}
                          />
                          <span className="text-xs text-[#5C6C62]">USD</span>
                        </div>
                      </div>
                    ))}
                  </div>
                  <p className="text-xs text-[#5C6C62] mt-2">Tip: Si necesitas anadir o eliminar zonas completas (no solo cambiar precios), contacta soporte tecnico.</p>
                </div>

                <Button
                  className="bg-[#B85042] hover:bg-[#B85042]/90 text-white rounded-xl"
                  onClick={handleSaveSettings}
                  disabled={savingSettings}
                  data-testid="save-settings-button"
                >
                  {savingSettings ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                  Guardar Configuracion
                </Button>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>

        {/* Substitution Modal */}
        {substitutionOrder && (
          <div className="fixed inset-0 bg-black/50 z-[60] flex items-center justify-center p-4" onClick={(e) => e.target === e.currentTarget && setSubstitutionOrder(null)}>
            <Card className="w-full max-w-md bg-white rounded-2xl" data-testid="substitution-modal">
              <CardHeader>
                <CardTitle className="font-heading text-[#0F2922]">⚠️ Marcar item como agotado</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <p className="text-sm text-[#5C6C62]">Pedido <b>#{substitutionOrder.id.slice(0,8)}</b> · {substitutionOrder.customer_name}</p>
                <div>
                  <Label className="text-[#0F2922] font-body block mb-2">¿Qué item se acabó?</Label>
                  <select value={subItemIndex} onChange={(e) => setSubItemIndex(parseInt(e.target.value))} className="w-full h-10 px-3 border border-[#E5E3D8] rounded-xl bg-white text-sm" data-testid="sub-item-select">
                    {(substitutionOrder.items || []).map((it, i) => (
                      <option key={i} value={i}>{it.name_es || it.name} x{it.quantity}{it.selected_option ? ` (${it.selected_option})` : ''} - ${it.subtotal?.toFixed(2)} MXN</option>
                    ))}
                  </select>
                </div>
                <div>
                  <Label className="text-[#0F2922] font-body block mb-2">Nota (opcional)</Label>
                  <Textarea value={subNote} onChange={(e) => setSubNote(e.target.value)} className="border-[#E5E3D8] rounded-xl" rows={3} placeholder="Ej: Lo sentimos, se agotó el día de hoy. ¿Quieres cambiar por otro o reembolso?" data-testid="sub-note-input" />
                </div>
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-xs text-blue-900">
                  📧 El cliente recibirá un email automático con un link para elegir entre <b>reembolso</b> o <b>cambio por otro item</b>.
                </div>
                <div className="flex gap-2">
                  <Button variant="outline" className="flex-1 rounded-xl" onClick={() => { setSubstitutionOrder(null); setSubNote(''); setSubItemIndex(0); }}>Cancelar</Button>
                  <Button className="flex-1 bg-orange-600 hover:bg-orange-700 text-white rounded-xl" onClick={async () => {
                    await handleUpdateOrderStatus(substitutionOrder.id, 'substitution_needed', { affected_item_index: subItemIndex, note: subNote });
                    setSubstitutionOrder(null);
                    setSubNote('');
                    setSubItemIndex(0);
                  }} data-testid="sub-confirm">Notificar al cliente</Button>
                </div>
              </CardContent>
            </Card>
          </div>
        )}
      </div>
    </div>
  );
};

// Menu Item Form Component
const MenuItemForm = ({ item, onSave, onCancel }) => {
  const [form, setForm] = useState(() => {
    const base = {
      name_es: '', name_en: '', name_he: '',
      description_es: '', description_en: '', description_he: '',
      ingredients_es: '', ingredients_en: '', ingredients_he: '',
      price: 0, price_usd: '', category: 'main', image_url: '', available: true,
      options: [], option_groups: [],
    };
    if (!item) return base;
    return {
      ...base,
      ...item,
      ingredients_es: Array.isArray(item.ingredients_es) ? item.ingredients_es.join(', ') : (item.ingredients_es || ''),
      ingredients_en: Array.isArray(item.ingredients_en) ? item.ingredients_en.join(', ') : (item.ingredients_en || ''),
      ingredients_he: Array.isArray(item.ingredients_he) ? item.ingredients_he.join(', ') : (item.ingredients_he || ''),
      options: item.options || [],
      option_groups: item.option_groups || [],
    };
  });

  const handleSubmit = () => {
    const payload = {
      ...form,
      price: parseFloat(form.price) || 0,
      price_usd: form.price_usd ? parseFloat(form.price_usd) : null,
      ingredients_es: typeof form.ingredients_es === 'string' ? form.ingredients_es.split(',').map(s => s.trim()).filter(Boolean) : form.ingredients_es,
      ingredients_en: typeof form.ingredients_en === 'string' ? form.ingredients_en.split(',').map(s => s.trim()).filter(Boolean) : form.ingredients_en,
      ingredients_he: typeof form.ingredients_he === 'string' ? form.ingredients_he.split(',').map(s => s.trim()).filter(Boolean) : form.ingredients_he,
      options: form.options && form.options.length > 0 ? form.options : null,
      option_groups: form.option_groups && form.option_groups.length > 0 ? form.option_groups : null,
    };
    delete payload.id;
    delete payload.cartKey;
    onSave(payload);
  };

  const addOption = () => {
    setForm(p => ({ ...p, options: [...(p.options || []), { label_es: '', label_en: '', label_he: '', extra_price: 0, extra_price_usd: 0 }] }));
  };
  const removeOption = (idx) => {
    setForm(p => ({ ...p, options: p.options.filter((_, i) => i !== idx) }));
  };
  const updateOption = (idx, field, value) => {
    setForm(p => {
      const opts = [...(p.options || [])];
      opts[idx] = { ...opts[idx], [field]: value };
      return { ...p, options: opts };
    });
  };

  // --- option_groups (multi-group system) helpers ---
  const addGroup = () => {
    setForm(p => ({
      ...p,
      option_groups: [...(p.option_groups || []), {
        id: `grupo_${(p.option_groups || []).length + 1}`,
        label_es: '', label_en: '', label_he: '',
        type: 'single', required: false,
        options: [],
      }],
    }));
  };
  const removeGroup = (gi) => {
    setForm(p => ({ ...p, option_groups: (p.option_groups || []).filter((_, i) => i !== gi) }));
  };
  const updateGroup = (gi, field, value) => {
    setForm(p => {
      const gs = [...(p.option_groups || [])];
      gs[gi] = { ...gs[gi], [field]: value };
      return { ...p, option_groups: gs };
    });
  };
  const addGroupOption = (gi) => {
    setForm(p => {
      const gs = [...(p.option_groups || [])];
      const opts = [...(gs[gi].options || []), { label_es: '', label_en: '', label_he: '', extra_price: 0, extra_price_usd: 0 }];
      gs[gi] = { ...gs[gi], options: opts };
      return { ...p, option_groups: gs };
    });
  };
  const removeGroupOption = (gi, oi) => {
    setForm(p => {
      const gs = [...(p.option_groups || [])];
      gs[gi] = { ...gs[gi], options: gs[gi].options.filter((_, i) => i !== oi) };
      return { ...p, option_groups: gs };
    });
  };
  const updateGroupOption = (gi, oi, field, value) => {
    setForm(p => {
      const gs = [...(p.option_groups || [])];
      const opts = [...(gs[gi].options || [])];
      opts[oi] = { ...opts[oi], [field]: value };
      gs[gi] = { ...gs[gi], options: opts };
      return { ...p, option_groups: gs };
    });
  };

  const update = (field, value) => setForm(p => ({ ...p, [field]: value }));

  return (
    <Card className="bg-white border-[#E5E3D8] rounded-2xl mb-6" data-testid="menu-item-form">
      <CardHeader>
        <CardTitle className="font-heading text-[#0F2922]">{item ? 'Editar Platillo' : 'Nuevo Platillo'}</CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <div>
            <Label className="text-xs text-[#5C6C62]">Nombre (ES)</Label>
            <Input value={form.name_es} onChange={(e) => update('name_es', e.target.value)} className="border-[#E5E3D8] rounded-xl" />
          </div>
          <div>
            <Label className="text-xs text-[#5C6C62]">Name (EN)</Label>
            <Input value={form.name_en} onChange={(e) => update('name_en', e.target.value)} className="border-[#E5E3D8] rounded-xl" />
          </div>
          <div>
            <Label className="text-xs text-[#5C6C62]">שם (HE)</Label>
            <Input value={form.name_he} onChange={(e) => update('name_he', e.target.value)} className="border-[#E5E3D8] rounded-xl" dir="rtl" />
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <div>
            <Label className="text-xs text-[#5C6C62]">Descripcion (ES)</Label>
            <Input value={form.description_es} onChange={(e) => update('description_es', e.target.value)} className="border-[#E5E3D8] rounded-xl" />
          </div>
          <div>
            <Label className="text-xs text-[#5C6C62]">Description (EN)</Label>
            <Input value={form.description_en} onChange={(e) => update('description_en', e.target.value)} className="border-[#E5E3D8] rounded-xl" />
          </div>
          <div>
            <Label className="text-xs text-[#5C6C62]">תיאור (HE)</Label>
            <Input value={form.description_he} onChange={(e) => update('description_he', e.target.value)} className="border-[#E5E3D8] rounded-xl" dir="rtl" />
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <div>
            <Label className="text-xs text-[#5C6C62]">Ingredientes ES (separados por coma)</Label>
            <Input value={form.ingredients_es} onChange={(e) => update('ingredients_es', e.target.value)} className="border-[#E5E3D8] rounded-xl" />
          </div>
          <div>
            <Label className="text-xs text-[#5C6C62]">Ingredients EN (comma-separated)</Label>
            <Input value={form.ingredients_en} onChange={(e) => update('ingredients_en', e.target.value)} className="border-[#E5E3D8] rounded-xl" />
          </div>
          <div>
            <Label className="text-xs text-[#5C6C62]">מרכיבים HE (מופרד בפסיק)</Label>
            <Input value={form.ingredients_he} onChange={(e) => update('ingredients_he', e.target.value)} className="border-[#E5E3D8] rounded-xl" dir="rtl" />
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
          <div>
            <Label className="text-xs text-[#5C6C62]">Precio (MXN)</Label>
            <Input type="number" value={form.price} onChange={(e) => update('price', e.target.value)} className="border-[#E5E3D8] rounded-xl" />
          </div>
          <div>
            <Label className="text-xs text-[#5C6C62]">Precio (USD)</Label>
            <Input type="number" value={form.price_usd || ''} onChange={(e) => update('price_usd', e.target.value)} className="border-[#E5E3D8] rounded-xl" placeholder="Opcional" />
          </div>
          <div>
            <Label className="text-xs text-[#5C6C62]">Categoria</Label>
            <Input value={form.category} onChange={(e) => update('category', e.target.value)} className="border-[#E5E3D8] rounded-xl" placeholder="main, soup, appetizer..." />
          </div>
          <div>
            <Label className="text-xs text-[#5C6C62]">URL Imagen</Label>
            <Input value={form.image_url} onChange={(e) => update('image_url', e.target.value)} className="border-[#E5E3D8] rounded-xl" />
          </div>
        </div>
        {/* Option Groups (nuevo: opciones agrupadas con tipo y requerido) */}
        <div className="border-t border-[#E5E3D8] pt-3">
          <div className="flex items-center justify-between mb-2">
            <Label className="text-sm font-medium text-[#0F2922]">Grupos de opciones (ej: Presentacion, Proteina, Toppings)</Label>
            <Button size="sm" variant="ghost" className="text-[#B85042] text-xs" onClick={addGroup} data-testid="add-option-group">+ Agregar grupo</Button>
          </div>
          {(form.option_groups || []).map((g, gi) => (
            <div key={gi} className="mb-3 p-3 bg-[#FAF9F6] rounded-xl border border-[#E5E3D8]" data-testid={`option-group-edit-${gi}`}>
              {/* Group header */}
              <div className="grid grid-cols-2 md:grid-cols-6 gap-2 mb-2 items-end">
                <div>
                  <Label className="text-[10px] text-[#5C6C62]">ID interno</Label>
                  <Input value={g.id || ''} onChange={(e) => updateGroup(gi, 'id', e.target.value)} className="border-[#E5E3D8] rounded-lg text-xs h-8" />
                </div>
                <div>
                  <Label className="text-[10px] text-[#5C6C62]">Etiqueta ES</Label>
                  <Input value={g.label_es || ''} onChange={(e) => updateGroup(gi, 'label_es', e.target.value)} className="border-[#E5E3D8] rounded-lg text-xs h-8" />
                </div>
                <div>
                  <Label className="text-[10px] text-[#5C6C62]">EN</Label>
                  <Input value={g.label_en || ''} onChange={(e) => updateGroup(gi, 'label_en', e.target.value)} className="border-[#E5E3D8] rounded-lg text-xs h-8" />
                </div>
                <div>
                  <Label className="text-[10px] text-[#5C6C62]">HE</Label>
                  <Input value={g.label_he || ''} onChange={(e) => updateGroup(gi, 'label_he', e.target.value)} className="border-[#E5E3D8] rounded-lg text-xs h-8" dir="rtl" />
                </div>
                <div>
                  <Label className="text-[10px] text-[#5C6C62]">Tipo</Label>
                  <select
                    value={g.type || 'single'}
                    onChange={(e) => updateGroup(gi, 'type', e.target.value)}
                    className="border border-[#E5E3D8] rounded-lg text-xs h-8 w-full px-2 bg-white"
                  >
                    <option value="single">Elegir una</option>
                    <option value="multi">Elegir varias</option>
                  </select>
                </div>
                <div className="flex items-end gap-2 h-full">
                  <label className="flex items-center gap-1 text-xs text-[#0F2922] cursor-pointer">
                    <input type="checkbox" checked={!!g.required} onChange={(e) => updateGroup(gi, 'required', e.target.checked)} />
                    Obligatorio
                  </label>
                  <Button size="sm" variant="ghost" className="text-red-500 h-8 w-8 p-0 ml-auto" onClick={() => removeGroup(gi)} data-testid={`remove-group-${gi}`}>
                    <Trash2 className="w-3 h-3" />
                  </Button>
                </div>
              </div>
              {/* Group options */}
              <div className="mt-2 ml-3 pl-3 border-l-2 border-[#B85042]/30">
                <div className="flex items-center justify-between mb-1">
                  <Label className="text-[10px] text-[#5C6C62] uppercase tracking-wider">Opciones de este grupo</Label>
                  <Button size="sm" variant="ghost" className="text-[#B85042] text-[10px] h-6" onClick={() => addGroupOption(gi)} data-testid={`add-group-option-${gi}`}>+ Anadir</Button>
                </div>
                {(g.options || []).map((opt, oi) => (
                  <div key={oi} className="flex flex-wrap items-center gap-1 mb-1">
                    <Input placeholder="ES" value={opt.label_es || ''} onChange={(e) => updateGroupOption(gi, oi, 'label_es', e.target.value)} className="border-[#E5E3D8] rounded-md text-xs h-7 flex-1 min-w-[80px]" />
                    <Input placeholder="EN" value={opt.label_en || ''} onChange={(e) => updateGroupOption(gi, oi, 'label_en', e.target.value)} className="border-[#E5E3D8] rounded-md text-xs h-7 flex-1 min-w-[80px]" />
                    <Input placeholder="HE" value={opt.label_he || ''} onChange={(e) => updateGroupOption(gi, oi, 'label_he', e.target.value)} className="border-[#E5E3D8] rounded-md text-xs h-7 w-20" dir="rtl" />
                    <Input type="number" placeholder="+MXN" value={opt.extra_price || ''} onChange={(e) => updateGroupOption(gi, oi, 'extra_price', parseFloat(e.target.value) || 0)} className="border-[#E5E3D8] rounded-md text-xs h-7 w-20" data-testid={`group-${gi}-opt-${oi}-mxn`} />
                    <Input type="number" placeholder="+USD" value={opt.extra_price_usd || ''} onChange={(e) => updateGroupOption(gi, oi, 'extra_price_usd', parseFloat(e.target.value) || 0)} className="border-[#E5E3D8] rounded-md text-xs h-7 w-20" />
                    <Button size="sm" variant="ghost" className="text-red-500 h-7 w-7 p-0" onClick={() => removeGroupOption(gi, oi)}>
                      <Trash2 className="w-3 h-3" />
                    </Button>
                  </div>
                ))}
                {(!g.options || g.options.length === 0) && (
                  <p className="text-[10px] text-[#5C6C62] italic">Sin opciones aun. Haz clic en &quot;+ Anadir&quot;.</p>
                )}
              </div>
            </div>
          ))}
          {(!form.option_groups || form.option_groups.length === 0) && (
            <p className="text-xs text-[#5C6C62] italic">Sin grupos de opciones. Util para platillos que requieren elegir proteina, presentacion, toppings, etc.</p>
          )}
        </div>

        {/* Legacy Options (un solo grupo simple) — solo se muestra si el platillo no usa grupos nuevos */}
        {(!form.option_groups || form.option_groups.length === 0) && (
          <div className="border-t border-[#E5E3D8] pt-3">
            <div className="flex items-center justify-between mb-2">
              <Label className="text-sm font-medium text-[#0F2922]">Opciones simples (legacy - una sola eleccion)</Label>
              <Button size="sm" variant="ghost" className="text-[#B85042] text-xs" onClick={addOption}>+ Agregar opcion</Button>
            </div>
            {(form.options || []).map((opt, idx) => (
              <div key={idx} className="flex items-center gap-2 mb-2 p-2 bg-[#FAF9F6] rounded-lg">
                <Input placeholder="ES" value={opt.label_es} onChange={(e) => updateOption(idx, 'label_es', e.target.value)} className="border-[#E5E3D8] rounded-lg text-xs flex-1" />
                <Input placeholder="EN" value={opt.label_en} onChange={(e) => updateOption(idx, 'label_en', e.target.value)} className="border-[#E5E3D8] rounded-lg text-xs flex-1" />
                <Input placeholder="HE" value={opt.label_he} onChange={(e) => updateOption(idx, 'label_he', e.target.value)} className="border-[#E5E3D8] rounded-lg text-xs w-20" dir="rtl" />
                <Input type="number" placeholder="+MXN" value={opt.extra_price || ''} onChange={(e) => updateOption(idx, 'extra_price', parseFloat(e.target.value) || 0)} className="border-[#E5E3D8] rounded-lg text-xs w-16" />
                <Input type="number" placeholder="+USD" value={opt.extra_price_usd || ''} onChange={(e) => updateOption(idx, 'extra_price_usd', parseFloat(e.target.value) || 0)} className="border-[#E5E3D8] rounded-lg text-xs w-16" />
                <Button size="sm" variant="ghost" className="text-red-500 h-8 w-8 p-0" onClick={() => removeOption(idx)}>
                  <Trash2 className="w-3 h-3" />
                </Button>
              </div>
            ))}
          </div>
        )}
        <div className="flex gap-3 pt-2">
          <Button variant="outline" className="border-[#E5E3D8] rounded-xl" onClick={onCancel}>Cancelar</Button>
          <Button className="bg-[#B85042] hover:bg-[#B85042]/90 text-white rounded-xl" onClick={handleSubmit} data-testid="save-menu-item">
            <Save className="w-4 h-4 mr-2" /> Guardar
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

export default AdminPage;
